// Path builders. A PathFn adds subpaths to the current path (it never calls beginPath), so the passes can
// trace several parts into one path (halo, glow) or one part at a time (outline, fill, shading). Paths are
// traced under the current transform, which is how parts are placed, rotated and offset.
import type { Ctx2D } from './ctx.ts';
import type { Rand } from './rng.ts';

export type PathFn = (c: Ctx2D) => void;
export type Pt = readonly [number, number];

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;

export function box(x: number, y: number, w: number, h: number): Box {
  return { x, y, w, h };
}

export function rrect(
  x: number,
  y: number,
  w: number,
  h: number,
  r: number | readonly number[],
): PathFn {
  const rs = typeof r === 'number' ? [r, r, r, r] : r;
  const lim = Math.min(w, h) / 2;
  const [tl, tr, br, bl] = rs.map((v) => Math.max(0, Math.min(lim, v))) as [
    number,
    number,
    number,
    number,
  ];
  return (c) => {
    c.moveTo(x + tl, y);
    c.lineTo(x + w - tr, y);
    if (tr > 0) c.arcTo(x + w, y, x + w, y + tr, tr);
    c.lineTo(x + w, y + h - br);
    if (br > 0) c.arcTo(x + w, y + h, x + w - br, y + h, br);
    c.lineTo(x + bl, y + h);
    if (bl > 0) c.arcTo(x, y + h, x, y + h - bl, bl);
    c.lineTo(x, y + tl);
    if (tl > 0) c.arcTo(x, y, x + tl, y, tl);
    c.closePath();
  };
}

export function circle(cx: number, cy: number, r: number): PathFn {
  return (c) => {
    c.moveTo(cx + r, cy);
    c.arc(cx, cy, r, 0, TAU);
    c.closePath();
  };
}

export function ellipse(cx: number, cy: number, rx: number, ry: number, rot = 0): PathFn {
  return (c) => {
    c.moveTo(cx + rx * Math.cos(rot), cy + rx * Math.sin(rot));
    c.ellipse(cx, cy, rx, ry, rot, 0, TAU);
    c.closePath();
  };
}

export function poly(pts: readonly Pt[]): PathFn {
  return (c) => {
    const [first, ...rest] = pts;
    if (!first) return;
    c.moveTo(first[0], first[1]);
    for (const p of rest) c.lineTo(p[0], p[1]);
    c.closePath();
  };
}

/** A polygon with every corner rounded by r (arcTo), for soft toy shapes. */
export function roundPoly(pts: readonly Pt[], r: number): PathFn {
  return (c) => {
    const n = pts.length;
    if (n < 3) return;
    const at = (i: number) => pts[((i % n) + n) % n] as Pt;
    const a = at(n - 1);
    const b = at(0);
    c.moveTo((a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
    for (let i = 0; i < n; i++) {
      const p = at(i);
      const q = at(i + 1);
      c.arcTo(p[0], p[1], (p[0] + q[0]) / 2, (p[1] + q[1]) / 2, r);
    }
    c.closePath();
  };
}

/** A closed smooth curve through the midpoints of a polygon (quadratic corners). */
export function smoothPoly(pts: readonly Pt[]): PathFn {
  return (c) => {
    const n = pts.length;
    if (n < 3) return;
    const at = (i: number) => pts[((i % n) + n) % n] as Pt;
    const m = (i: number): Pt => {
      const p = at(i);
      const q = at(i + 1);
      return [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
    };
    const s = m(n - 1);
    c.moveTo(s[0], s[1]);
    for (let i = 0; i < n; i++) {
      const p = at(i);
      const e = m(i);
      c.quadraticCurveTo(p[0], p[1], e[0], e[1]);
    }
    c.closePath();
  };
}

/** Seeded jittered rock outline: n points around an ellipse, radius jittered by ±jit. */
export function blobPts(
  rng: Rand,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  n: number,
  jit: number,
  rot = 0,
): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * TAU + (rng() - 0.5) * (TAU / n) * 0.5;
    const k = 1 + (rng() * 2 - 1) * jit;
    out.push([cx + Math.cos(a) * rx * k, cy + Math.sin(a) * ry * k]);
  }
  return out;
}

export function starPts(
  cx: number,
  cy: number,
  ro: number,
  ri: number,
  n: number,
  rot = -90,
): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? ro : ri;
    const a = (rot + (i * 180) / n) * DEG;
    out.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
  }
  return out;
}

export function regularPts(cx: number, cy: number, r: number, n: number, rot = -90): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const a = (rot + (i * 360) / n) * DEG;
    out.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
  }
  return out;
}

/** A trapezoid: top edge wTop, bottom edge wBot, centred on cx. */
export function trapPts(cx: number, y: number, wTop: number, wBot: number, h: number): Pt[] {
  return [
    [cx - wTop / 2, y],
    [cx + wTop / 2, y],
    [cx + wBot / 2, y + h],
    [cx - wBot / 2, y + h],
  ];
}

/** A stadium from (x1,y1) to (x2,y2) with radius r. */
export function capsule(x1: number, y1: number, x2: number, y2: number, r: number): PathFn {
  const a = Math.atan2(y2 - y1, x2 - x1);
  return (c) => {
    c.moveTo(x1 + Math.cos(a + Math.PI / 2) * r, y1 + Math.sin(a + Math.PI / 2) * r);
    c.arc(x1, y1, r, a + Math.PI / 2, a + (3 * Math.PI) / 2);
    c.arc(x2, y2, r, a - Math.PI / 2, a + Math.PI / 2);
    c.closePath();
  };
}

/** Several paths as one. */
export function union(...ps: PathFn[]): PathFn {
  return (c) => {
    for (const p of ps) p(c);
  };
}

/** The path under an extra transform (applied only while tracing). */
export function moved(p: PathFn, dx: number, dy: number): PathFn {
  return (c) => {
    c.save();
    c.translate(dx, dy);
    p(c);
    c.restore();
  };
}

export function rotated(p: PathFn, cx: number, cy: number, deg: number): PathFn {
  return (c) => {
    c.save();
    c.translate(cx, cy);
    c.rotate(deg * DEG);
    c.translate(-cx, -cy);
    p(c);
    c.restore();
  };
}

export function bboxOf(pts: readonly Pt[]): Box {
  let x0 = Number.POSITIVE_INFINITY;
  let y0 = Number.POSITIVE_INFINITY;
  let x1 = Number.NEGATIVE_INFINITY;
  let y1 = Number.NEGATIVE_INFINITY;
  for (const [x, y] of pts) {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

// ---- immediate helpers -----------------------------------------------------------------------------------

export function fillPath(c: Ctx2D, p: PathFn, color: string): void {
  c.beginPath();
  p(c);
  c.fillStyle = color;
  c.fill();
}

export function strokePath(
  c: Ctx2D,
  p: PathFn,
  color: string,
  width: number,
  cap: CanvasLineCap = 'round',
): void {
  c.beginPath();
  p(c);
  c.strokeStyle = color;
  c.lineWidth = width;
  c.lineJoin = 'round';
  c.lineCap = cap;
  c.stroke();
}

/** An open polyline (not closed), for strings, stripes and glyph strokes. */
export function line(pts: readonly Pt[]): PathFn {
  return (c) => {
    const [first, ...rest] = pts;
    if (!first) return;
    c.moveTo(first[0], first[1]);
    for (const p of rest) c.lineTo(p[0], p[1]);
  };
}
